import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  fetchLegacy360ReportsList,
  Legacy360ReportsApi,
} from "../api/Legacy360Api";
import { formattedDate } from "../utils";
import Table from "./Table";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "./helper-components/TableModels";
import { ReportSummaryItemApi } from "../api/Reports";
import Loading from "../shared-components/Loading";
import Pagination from "../shared-components/Pagination";
import { genericErrorLoadingMessage } from "./utils";
import { useLegacy360ReportsTabVisibility } from "./useLegacy360ReportsTabVisibility";
import Legacy360InsightsCircle from "./helper-components/Legacy360InsightsCircle";
import Legacy360DownloadActions from "./Legacy360DownloadActions";

const PAGE_SIZE = 20;

/**
 * Same flex model as {@link ./ReportsCompleted} (`flex-1` columns). Each column pairs
 * `headerClass` with `cellClass` so label and values share justify/text alignment and
 * only Table’s `p-3` / `px-3` handle horizontal inset (no extra `!pl-*` on body alone).
 */
const LEGACY_360_COLUMNS = [
  {
    label: "Name",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center text-left overflow-hidden text-black",
  },
  {
    label: "Insights",
    headerClass:
      "flex-1 min-w-16 shrink-0 basis-0 items-center justify-center text-center",
    cellClass:
      "flex-1 min-w-16 shrink-0 basis-0 items-center justify-center text-center overflow-visible",
  },
  {
    label: "Date Added",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left tabular-nums",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center text-left tabular-nums overflow-hidden",
  },
  {
    label: "Date Completed",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left tabular-nums",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center text-left tabular-nums overflow-hidden",
  },
  {
    label: "Blueprint",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left !pr-6",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center text-left overflow-hidden !pr-6",
  },
  {
    label: "Company",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left",
    cellClass: "flex-1 min-w-0 basis-0 items-center text-left overflow-hidden",
  },
  {
    label: "Client ID",
    headerClass: "flex-1 min-w-0 basis-0 items-center text-left !px-0",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center text-left overflow-hidden !px-0",
  },
  {
    label: "Actions",
    headerClass:
      "flex-1 min-w-0 basis-0 items-center justify-center text-center",
    cellClass:
      "flex-1 min-w-0 basis-0 items-center justify-center text-center overflow-visible",
  },
] as const;

function colClass(i: number): string {
  return LEGACY_360_COLUMNS[i].cellClass;
}

/** Plain text in a COMPONENT cell so `Table` applies `customClass` without changing shared TEXT handling. */
function legacy360TextCell(text: string, columnIndex: number): ReportEntry {
  return {
    text: "",
    style: ReportEntryStyle.COMPONENT,
    customClass: colClass(columnIndex),
    component: <span className="min-w-0 truncate block w-full">{text}</span>,
  };
}

function buildRows(
  results: Legacy360ReportsApi[]
): ReportRowItem<ReportSummaryItemApi>[] {
  return results.map((row) => {
    const dateAdded =
      row.report_created != null ? formattedDate(row.report_created) : "—";
    const dateCompleted =
      row.report_finished != null ? formattedDate(row.report_finished) : "—";
    const data: ReportEntry[] = [
      legacy360TextCell(row.person_name ?? "", 0),
      {
        text: "",
        style: ReportEntryStyle.COMPONENT,
        customClass: colClass(1),
        component: (
          <Legacy360InsightsCircle
            insights={row.insights as Record<string, unknown> | null}
            rowKey={row.report_uuid}
          />
        ),
      },
      legacy360TextCell(dateAdded, 2),
      legacy360TextCell(dateCompleted, 3),
      legacy360TextCell(row.blueprint_name, 4),
      legacy360TextCell(row.company_name, 5),
      legacy360TextCell(row.assignment_number?.trim() ?? "", 6),
      {
        text: "",
        style: ReportEntryStyle.COMPONENT,
        customClass: colClass(7),
        component: <Legacy360DownloadActions reportUuid={row.report_uuid} />,
      },
    ];
    return {
      report_uuid: row.report_uuid,
      data,
    };
  });
}

function parsePageOneBased(raw: string | null): number {
  const n = Number.parseInt(raw || "1", 10);
  if (!Number.isFinite(n) || n < 1) {
    return 1;
  }
  return n;
}

export default function Reports360Legacy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { requestApi } = useAppContext();
  const { canManageReports } = usePermissionValidator();
  const visibilityQuery = useLegacy360ReportsTabVisibility();

  const filter = (searchParams.get("search") ?? "").trim();
  const page = parsePageOneBased(searchParams.get("page"));

  useEffect(() => {
    if (!canManageReports) {
      navigate("/reports", { replace: true });
    }
  }, [canManageReports, navigate]);

  useEffect(() => {
    if (!canManageReports) return;
    if (!visibilityQuery.isFetched || visibilityQuery.isFetching) return;
    const allowed =
      visibilityQuery.isSuccess && visibilityQuery.data?.visible === true;
    if (!allowed) navigate("/reports", { replace: true });
  }, [
    canManageReports,
    navigate,
    visibilityQuery.isFetched,
    visibilityQuery.isFetching,
    visibilityQuery.isSuccess,
    visibilityQuery.data,
  ]);

  const listQuery = useQuery(
    ["legacy360ReportsList", page, PAGE_SIZE, filter],
    () =>
      fetchLegacy360ReportsList(requestApi, {
        page: page - 1,
        page_size: PAGE_SIZE,
        search: filter.trim() || undefined,
      }),
    {
      enabled:
        Boolean(requestApi) &&
        canManageReports &&
        visibilityQuery.isSuccess &&
        visibilityQuery.data?.visible === true,
      keepPreviousData: true,
    }
  );

  const tableHeader = useMemo(
    () =>
      LEGACY_360_COLUMNS.map(({ label, headerClass }) => ({
        text: label,
        customClass: headerClass,
      })),
    []
  );

  const rows = useMemo(
    () => buildRows(listQuery.data?.results ?? []),
    [listQuery.data?.results]
  );

  /** Initial load uses `isLoading`; search/page changes keep prior rows until fetch completes (`keepPreviousData`), so use `isFetching` + `isPreviousData`. */
  const isListPending =
    listQuery.isLoading || (listQuery.isFetching && listQuery.isPreviousData);

  if (!canManageReports) return null;

  if (
    visibilityQuery.isLoading ||
    visibilityQuery.isIdle ||
    (visibilityQuery.isFetching && !visibilityQuery.data)
  ) {
    return (
      <div className="pt-8">
        <Loading />
      </div>
    );
  }

  if (listQuery.isError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  const total = listQuery.data?.metadata.total_results ?? 0;

  return (
    <div className="space-y-4">
      <Table data={rows} header={tableHeader} isLoading={isListPending} />
      {!isListPending && listQuery.data && (
        <Pagination
          pageSize={PAGE_SIZE}
          currentPage={page}
          totalCount={total}
          search={filter}
        />
      )}
    </div>
  );
}
